/**
 * Labstep
 *
 * @module containers/Entity/BatchProcessCreate
 * @desc Container for creating entities with batch processing
 */

import { BatchRequestProgress } from 'labstep-web/core/BatchRequest/Progress';
import type { EntityNameMap } from 'labstep-web/models';
import {
  DEFAULT_MAX_BATCH_SIZE,
  processBatches,
} from 'labstep-web/services/batch.service';
import type { EntityName } from 'labstep-web/services/schema/schema';
import {
  selectCreatedEntity,
  selectCreateEntityStatus,
} from 'labstep-web/state/selectors/entity';
import type { LabstepReduxState } from 'labstep-web/state/types';
import React, { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { connect, useDispatch, useStore } from 'react-redux';
import type { Dispatch } from 'redux';
import { v4 } from 'uuid';
import type {
  Batch,
  BatchProcessCreateContainerOwnProps,
  IUuidContainerProps,
} from './types';

export type EntityCreateChildrenProps = ReturnType<
  typeof mapStateToProps
> & {
  create: (
    body: any,
    options?: any,
    parentName?: string,
    parentId?: number | string,
  ) => Promise<void>;
  retry: (batches: Batch<keyof EntityNameMap>[]) => Promise<void>;
};

const Container = <P extends EntityCreateChildrenProps>({
  children,
  retry,
  refreshBatchUuids,
  batches,
  batchStatuses,
  title,
  ...rest
}: P & {
  children: (
    childrenProps: Omit<
      P,
      'children' | 'retry' | 'refreshBatchUuids' | 'batches' | 'title'
    >,
  ) => JSX.Element | null;
}) => {
  const retryRequests = React.useCallback(
    (requestIds: string[]) => {
      const retryBatches = batches.filter((batch) =>
        requestIds.includes(batch.batchUuid),
      );
      retry(retryBatches);
    },
    [batches],
  );

  const cancelRequests = React.useCallback((requestIds: string[]) => {
    refreshBatchUuids(requestIds);
  }, []);

  if (batchStatuses.length > 0) {
    const successfulRequests = batchStatuses.filter(
      (batchStatus) =>
        batchStatus.status !== undefined &&
        !batchStatus.status.isFetching &&
        batchStatus.status.error === null,
    );
    const failedRequests = batchStatuses.filter(
      (batchStatus) =>
        batchStatus.status !== undefined &&
        batchStatus.status.error !== null,
    );

    const totalItems = batches.reduce(
      (count, batch) => count + batch.items.length,
      0,
    );
    const successfulItemsCount = successfulRequests.reduce(
      (count, batchStatus) => {
        const batch = batches.find(
          (b) => b.batchUuid === batchStatus.batchUuid,
        );
        return count + (batch ? batch.items.length : 0);
      },
      0,
    );
    const noErrors = batchStatuses.every(
      (batchStatus) =>
        batchStatus.status !== undefined &&
        !batchStatus.status.isFetching &&
        batchStatus.status.error === null,
    );

    if (successfulItemsCount === totalItems || noErrors) {
      return children({ ...rest, batchStatuses } as any);
    }

    return (
      <>
        {children({ ...rest, batchStatuses } as any)}
        {createPortal(
          <BatchRequestProgress
            title={title}
            total={totalItems}
            successCount={successfulItemsCount}
            requests={batches}
            failedRequests={failedRequests}
            retryRequests={retryRequests}
            cancelRequests={cancelRequests}
          />,
          document.body,
        )}
      </>
    );
  }
  return children({ ...rest, batchStatuses } as any);
};

const mapStateToProps = <T extends EntityName>(
  state: LabstepReduxState,
  ownProps: BatchProcessCreateContainerOwnProps<T>,
) => {
  const batchStatuses = ownProps.batchUuids.map((batchUuid) => ({
    batchUuid,
    status: selectCreateEntityStatus(
      state,
      ownProps.entityName,
      batchUuid,
    ),
  }));

  const entities = ownProps.batchUuids.map((batchUuid) =>
    selectCreatedEntity(state, ownProps.entityName, batchUuid),
  );

  return {
    batchStatuses,
    entities,
    batches: ownProps.batches,
    refreshBatchUuids: ownProps.refreshBatchUuids,
    title: ownProps.title,
  };
};

const ConnectedChildren = connect<any, any, any>(mapStateToProps)(
  Container,
);

// Component that provides create and retry functions using hooks
const WithDispatchFunctions = (props: any) => {
  const dispatch = useDispatch();
  const store = useStore<LabstepReduxState>();

  const create = useCallback(
    async (
      body: any,
      options: any = {},
      parentName?: string,
      parentId?: number | string,
    ) => {
      const {
        maxBatchSize = DEFAULT_MAX_BATCH_SIZE,
        setBatchUuids,
        setBatches,
      } = props;

      const enhancedOptions = {
        ...options,
        batch: true,
        toast: false,
      };

      if (!Array.isArray(body) || body.length === 0) {
        return;
      }

      const batches: Batch<keyof EntityNameMap>[] = [];
      for (let i = 0; i < body.length; i += maxBatchSize) {
        batches.push({
          batchUuid: v4(),
          items: body.slice(i, i + maxBatchSize),
          parentName,
          parentId,
        });
      }

      setBatchUuids(batches.map((batch) => batch.batchUuid));
      setBatches(batches);

      await processBatches(
        dispatch,
        store,
        props,
        batches,
        enhancedOptions,
      )
        .then(() => {
          if (options && options.onSuccess) {
            options.onSuccess();
          }
        })
        .finally(() => {
          if (options && options.onEnd) {
            options.onEnd();
          }
        });
    },
    [dispatch, store, props],
  );

  const retry = useCallback(
    async (batches: Batch<keyof EntityNameMap>[]) => {
      const options = { batch: true, toast: false };

      if (!Array.isArray(batches) || batches.length === 0) {
        return;
      }

      await processBatches(dispatch, store, props, batches, options);
    },
    [dispatch, store, props],
  );

  return (
    <ConnectedChildren {...props} create={create} retry={retry} />
  );
};

export const UuidContainer: React.FC<
  IUuidContainerProps<any> & {
    children: (
      childrenProps: Omit<
        EntityCreateChildrenProps,
        'batches' | 'refreshBatchUuids' | 'retry' | 'title'
      >,
    ) => JSX.Element | null;
  }
> = ({ ...props }) => {
  const [batchUuids, setBatchUuids] = useState<string[]>([]);
  const [batches, setBatches] = useState<
    Batch<keyof EntityNameMap>[]
  >([]);

  const refreshBatchUuids = useCallback((values: string[]) => {
    // Remove values that already exist in batchUuids
    setBatchUuids((prevBatchUuids) =>
      prevBatchUuids.filter((uuid) => !values.includes(uuid)),
    );
  }, []);

  return (
    <WithDispatchFunctions
      {...props}
      batchUuids={batchUuids}
      setBatchUuids={setBatchUuids}
      batches={batches}
      setBatches={setBatches}
      refreshBatchUuids={refreshBatchUuids}
    />
  );
};

export const EntityBatchProcessCreateContainer = UuidContainer;
